import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Swiper } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import useNavigation from "../../hooks/useNavigation";

type SwiperRef = {
  swipeTo: (index: number) => void;
  swipeNext: () => void;
  swipePrev: () => void;
};

const mobileBg = require("../../assets/images/totarial/mobile.png");

const buyImgs = [
  require("../../assets/images/totarial/buy/1.png"),
  require("../../assets/images/totarial/buy/2.png"),
  require("../../assets/images/totarial/buy/3.png"),
  require("../../assets/images/totarial/buy/4.png"),
  require("../../assets/images/totarial/buy/5.png"),
  require("../../assets/images/totarial/buy/6.png"),
  require("../../assets/images/totarial/buy/7.png"),
  require("../../assets/images/totarial/buy/8.png"),
];

const sellImgs = [
  require("../../assets/images/totarial/sell/1.png"),
  require("../../assets/images/totarial/sell/2.png"),
  require("../../assets/images/totarial/sell/3.png"),
  require("../../assets/images/totarial/sell/4.png"),
  require("../../assets/images/totarial/sell/5.png"),
  require("../../assets/images/totarial/sell/6.png"),
  require("../../assets/images/totarial/sell/7.png"),
  require("../../assets/images/totarial/sell/8.png"),
  require("../../assets/images/totarial/sell/9.png"),
];

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  display: flex;
  flex-direction: column;
  background: #f7f8ff;
  min-height: 0;
`;

const Tabs = styled.div`
  flex-shrink: 0;
  display: flex;
  margin: 10px 15px 0;
  border-radius: 6px;
  padding: 2px;
  background: #e6e7f2;
`;

const Tab = styled.div<{ $active?: boolean }>`
  flex: 1;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.blockBackground : "transparent"};
`;

const Stage = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 8px 16px;
`;

const NavBtn = styled.button<{ $disabled?: boolean }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border || "#ddd"};
  background: #fff;
  color: ${({ theme, $disabled }) =>
    $disabled ? "#ccc" : theme.colors.primary};
  font-size: 22px;
  line-height: 1;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  &:active {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.75)};
  }
`;

const Phone = styled.div`
  width: min(294px, 70vw);
  height: min(600px, calc(100% - 8px));
  background: url(${mobileBg}) center / contain no-repeat;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Screen = styled.div`
  width: 84%;
  height: 78%;
  margin-top: 2%;
  overflow: hidden;
  border-radius: 8px;
`;

const SlideImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #fff;
`;

const Footer = styled.div`
  flex-shrink: 0;
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 0 16px 16px;
`;

const resolveTab = (params: Record<string, any> = {}): "buy" | "sell" => {
  const kind = String(params.kind || "").toLowerCase();
  const title = String(params.title || "");
  if (kind === "sell" || title.includes("出售") || title.includes("卖出")) {
    return "sell";
  }
  return "buy";
};

const TutorialMap = () => {
  const { params, navigate } = useNavigation();

  const swiperRef = useRef<SwiperRef>(null);
  const [tab, setTab] = useState<"buy" | "sell">(() => resolveTab(params));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setTab(resolveTab(params));
  }, [params]);

  useEffect(() => {
    setIndex(0);
    swiperRef.current?.swipeTo(0);
  }, [tab]);

  const imgs = useMemo(() => (tab === "buy" ? buyImgs : sellImgs), [tab]);
  const pageTitle = tab === "buy" ? "购买教程" : "出售教程";
  const atStart = index <= 0;
  const atEnd = index >= imgs.length - 1;

  const onTabPress = (next: "buy" | "sell") => {
    if (next === tab) return;
    setTab(next);
    navigate("TutorialMap", { kind: next }, { replace: true });
  };

  const goPrev = () => {
    if (atStart) return;
    swiperRef.current?.swipePrev();
  };

  const goNext = () => {
    if (atEnd) return;
    swiperRef.current?.swipeNext();
  };

  return (
    <PageWrap>
      <PageHeader title={pageTitle} />
      <Body>
        <Tabs>
          <Tab $active={tab === "buy"} onClick={() => onTabPress("buy")}>
            购买
          </Tab>
          <Tab $active={tab === "sell"} onClick={() => onTabPress("sell")}>
            出售
          </Tab>
        </Tabs>
        <Stage>
          <NavBtn
            type="button"
            aria-label="上一步"
            $disabled={atStart}
            onClick={goPrev}
          >
            ‹
          </NavBtn>
          <Phone>
            <Screen>
              <Swiper
                key={tab}
                ref={swiperRef}
                style={{ width: "100%", height: "100%" }}
                indicator={() => null}
                defaultIndex={0}
                onIndexChange={setIndex}
              >
                {imgs.map((src, i) => (
                  <Swiper.Item key={`${tab}-${i}`}>
                    <SlideImg src={src} alt={`${pageTitle}-${i + 1}`} />
                  </Swiper.Item>
                ))}
              </Swiper>
            </Screen>
          </Phone>
          <NavBtn
            type="button"
            aria-label="下一步"
            $disabled={atEnd}
            onClick={goNext}
          >
            ›
          </NavBtn>
        </Stage>
        <Footer>
          {index + 1} / {imgs.length}
        </Footer>
      </Body>
    </PageWrap>
  );
};

export default observer(TutorialMap);
